import type { Media } from '@/payload-types'
import { extractPlainTextFromLexicalNodes } from '@/utilities/extractTocFromLexical'

import { getServerSideURL } from './getURL'
import { getMediaUrl } from './getMediaUrl'

function toAbsolutePublicUrl(href: string): string {
  if (!href) return ''
  if (/^https?:\/\//i.test(href)) return href
  const base = getServerSideURL().replace(/\/$/, '')
  const path = href.startsWith('/') ? href : `/${href}`
  return `${base}${path}`
}

/** Largura máxima típica da coluna de texto (prose ~ 48rem) para `sizes` do navegador. */
export const ARTICLE_INLINE_MEDIA_SIZES =
  '(max-width: 640px) 100vw, (max-width: 1024px) min(100vw - 2rem, 42rem), min(48rem, 65vw)'

export type ImageObjectLd = {
  '@type': 'ImageObject'
  url: string
  width?: number
  height?: number
  caption?: string
}

export function getMediaIntrinsicDimensions(resource: Media): { width: number; height: number } {
  const w =
    resource.width ??
    resource.sizes?.xlarge?.width ??
    resource.sizes?.large?.width ??
    resource.sizes?.medium?.width
  const h =
    resource.height ??
    resource.sizes?.xlarge?.height ??
    resource.sizes?.large?.height ??
    resource.sizes?.medium?.height
  if (w && h && w > 0 && h > 0) {
    return { width: w, height: h }
  }
  return { width: 1200, height: 675 }
}

/**
 * Objeto alinhado a schema.org `ImageObject` para compor JSON-LD no layout (ex.: `Article.image`).
 */
export function buildImageObjectFromMedia(resource: Media): ImageObjectLd | null {
  const path = resource.url ? getMediaUrl(resource.url, resource.updatedAt) : ''
  if (!path) return null
  const url = toAbsolutePublicUrl(path.split('?')[0] ?? path)
  const { width, height } = getMediaIntrinsicDimensions(resource)
  const caption = extractCaptionPlainText(resource.caption)
  const out: ImageObjectLd = {
    '@type': 'ImageObject',
    url,
    width,
    height,
  }
  if (caption) out.caption = caption
  return out
}

export function extractCaptionPlainText(caption: Media['caption'] | null | undefined): string {
  if (!caption?.root?.children?.length) return ''
  return extractPlainTextFromLexicalNodes(
    caption.root.children as Parameters<typeof extractPlainTextFromLexicalNodes>[0],
  )
}
